import { Pencil } from 'lucide-react';
import { observer } from 'mobx-react';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useWrapRequest } from 'use-wrap-request';
import { PageHeader } from '@components/page-header';
import { Spinner } from '@components/spinner';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@components/ui/card';
import { Api } from '../../core/api';
import { injectTSDI } from '../../core/tsdi';
import {
    ActivityPointsMultiplierTierDto,
    ActivityType
} from '../../core/api/FitnessActivityClient';
import { links } from '../../core/router';

function formatEnumLabel(value?: string | null): string {
    if (!value) {
        return '-';
    }

    return value
        .toLowerCase()
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

function formatTierRange(tier: ActivityPointsMultiplierTierDto): string {
    const min = tier.min.toLocaleString();
    const max =
        tier.max !== undefined && tier.max !== null
            ? tier.max.toLocaleString()
            : '∞';

    return `${min} - ${max}`;
}

function activityTypeBadgeClass(activityType: ActivityType): string {
    switch (activityType) {
        case ActivityType.WORKOUT:
            return 'border-chart-1 text-chart-1';
        case ActivityType.FACILITY_VISIT:
            return 'border-chart-2 text-chart-2';
        case ActivityType.DAILY_STEPS:
        default:
            return 'border-primary text-primary';
    }
}

const DetailRow = ({
    label,
    children
}: {
    label: string;
    children: React.ReactNode;
}) => (
    <div className="flex flex-col gap-1">
        <span className="text-xs font-medium text-muted-foreground">
            {label}
        </span>
        <div className="text-sm">{children}</div>
    </div>
);

const ActivityPointsRuleDetails = observer(() => {
    const { ruleId } = useParams();
    const navigate = useNavigate();
    const { fitnessActivityClient } = injectTSDI(Api);

    const ruleDetails = useWrapRequest(async (id: number) => {
        const result =
            await fitnessActivityClient.admin.activityPointsMultiplierTierGroupControllerGetDetails(
                id
            );
        return result.data;
    });

    React.useEffect(() => {
        if (ruleId) {
            ruleDetails.request([parseInt(ruleId, 10)]);
        }
    }, [ruleId]);

    if (ruleDetails.loading) {
        return (
            <div className="flex min-h-[400px] items-center justify-center">
                <Spinner />
            </div>
        );
    }

    if (ruleDetails.error || !ruleDetails.result) {
        return (
            <>
                <PageHeader title="Activity Points Rule Details" />
                <Alert variant="destructive">
                    <AlertDescription>
                        {ruleDetails.error?.message ||
                            'Failed to load rule details'}
                    </AlertDescription>
                </Alert>
            </>
        );
    }

    const rule = ruleDetails.result;

    const scopeTitle = rule.workoutType
        ? formatEnumLabel(rule.workoutType)
        : `${formatEnumLabel(rule.activityType)} Rule`;

    const scopeSubtitle = [
        `Category: ${rule.workoutCategory ? formatEnumLabel(rule.workoutCategory) : 'All'}`,
        `Type: ${rule.workoutType ? formatEnumLabel(rule.workoutType) : 'All'}`,
        `Recording: ${rule.recordingMethod ? formatEnumLabel(rule.recordingMethod) : 'All'}`
    ].join(' • ');

    return (
        <>
            <PageHeader
                title={scopeTitle}
                description={scopeSubtitle}
                actions={
                    <Button
                        onClick={() =>
                            navigate(links.editActivityPointsRule(ruleId!))
                        }
                    >
                        <Pencil />
                        Edit
                    </Button>
                }
            />

            <div className="grid gap-6 lg:grid-cols-3">
                <Card className="lg:col-span-1">
                    <CardHeader>
                        <CardTitle>Basic Information</CardTitle>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-4">
                        <DetailRow label="ID">{rule.id}</DetailRow>
                        <DetailRow label="Activity Type">
                            <Badge
                                variant="outline"
                                className={activityTypeBadgeClass(
                                    rule.activityType
                                )}
                            >
                                {formatEnumLabel(rule.activityType)}
                            </Badge>
                        </DetailRow>
                        <DetailRow label="Workout Category">
                            {rule.workoutCategory
                                ? formatEnumLabel(rule.workoutCategory)
                                : 'All'}
                        </DetailRow>
                        <DetailRow label="Workout Type">
                            {rule.workoutType
                                ? formatEnumLabel(rule.workoutType)
                                : 'All'}
                        </DetailRow>
                        <DetailRow label="Recording Method">
                            {rule.recordingMethod
                                ? formatEnumLabel(rule.recordingMethod)
                                : 'All'}
                        </DetailRow>
                        <DetailRow label="Required Average Speed (km/h)">
                            {rule.requiredAverageSpeed !== undefined &&
                            rule.requiredAverageSpeed !== null
                                ? rule.requiredAverageSpeed
                                : '-'}
                        </DetailRow>
                        <DetailRow label="Required Calories Per Minute">
                            {rule.requiredCaloriesPerMinute !== undefined &&
                            rule.requiredCaloriesPerMinute !== null
                                ? rule.requiredCaloriesPerMinute
                                : '-'}
                        </DetailRow>
                        <DetailRow label="Priority">{rule.priority}</DetailRow>
                    </CardContent>
                </Card>

                <Card className="lg:col-span-2">
                    <CardHeader>
                        <CardTitle>Multiplier Tiers</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {rule.tiers && rule.tiers.length > 0 ? (
                            <div className="flex flex-col gap-3">
                                {rule.tiers.map((tier, index) => (
                                    <div
                                        key={index}
                                        className="grid gap-4 rounded-md border bg-muted/40 p-4 sm:grid-cols-3"
                                    >
                                        <DetailRow label="Tier">
                                            <span className="font-medium">
                                                {index + 1}
                                            </span>
                                        </DetailRow>
                                        <DetailRow label="Range">
                                            <span className="font-medium">
                                                {formatTierRange(tier)}
                                            </span>
                                        </DetailRow>
                                        <DetailRow label="Multiplier">
                                            <span className="font-bold text-primary">
                                                {tier.multiplier}x
                                            </span>
                                        </DetailRow>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-muted-foreground">
                                No tiers configured
                            </p>
                        )}
                    </CardContent>
                </Card>
            </div>
        </>
    );
});

export default ActivityPointsRuleDetails;
